/**
 * PRD.218 + PRD.229 — Record a payment.
 *
 * Two modes:
 *   - **Invoice-locked** (default for callers passing `invoice`): the
 *     dialog operates against a specific invoice, shows total/paid/
 *     remaining, and enforces the no-overpayment cap. This is how the
 *     InvoicesTab drawer, BillingAcrossVisitsView row "Pay" action,
 *     and GlobalInvoicesPage row "Pay" action have always called it.
 *   - **Patient-level** (PRD.229): when `invoice` is null and
 *     `lockInvoice` is false, the dialog renders an optional invoice
 *     picker. The user can pick an invoice (then the locked-mode UX
 *     kicks in) or stay patient-level (no invoice link). Patient-level
 *     payments write `invoice_id=NULL` + `patient_id={patientId}` so
 *     `qPatientBillingAggregate` still folds them into the balance.
 *
 * Visit Date (PRD.229 §5.2/5.3) is always asked, defaults to today,
 * and overrides `paid_at` so the payment row is anchored to clinic
 * context rather than just the moment the user clicked Save.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { useAuth } from '@helios/auth-firebase-web';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import {
  qInvoicePaidTotalCents,
  qInvoiceItems,
  qInvoicePaymentsByInvoice,
  qInvoicesByPatient,
  aggregatePaymentAllocations,
  parsePaymentAllocations,
  type InvoicePaidTotalRow,
  type InvoiceItemRow,
  type InvoicePaymentRow,
  type InvoiceRow,
  type PaymentAllocation,
} from '@helios/data-access';
import {
  greaterThan,
  isPositive,
  parseAmountInput,
  phpFromCents,
  phpFromMajor,
  subtractMoney,
  toCents,
  type Money,
} from '@helios/domain';
import { useFormatMoney, useRegionalSettings } from '../../hooks/useFormatMoney';
import { itemChargeCents, inputToCents } from './payment-allocation-utils';
import { syncPatientPackagePaymentStatusFromInvoice } from '../../services/patient-package-service';

// ─── Types ───

export type PaymentMethod = 'cash' | 'card' | 'bank_transfer' | 'gcash' | 'other';

const METHOD_OPTIONS: ReadonlyArray<{ value: PaymentMethod; label: string }> = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'gcash', label: 'GCash' },
  { value: 'other', label: 'Other' },
];

interface InvoiceForPayment {
  id: string;
  invoice_no: string | null;
  /** Decimal-major value from invoices.grand_total. */
  grand_total: number | null;
  status: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-selected invoice. When null + `lockInvoice` is false, the dialog shows a picker. */
  invoice: InvoiceForPayment | null;
  /**
   * PRD.229 — required so the dialog can scope the optional invoice
   * picker AND stamp invoice_payments.patient_id directly (which the
   * patient billing aggregate uses to fold in patient-level payments).
   */
  patientId: string;
  /**
   * When true (default for invoice-scoped callers), the dialog stays
   * locked to the given invoice. When false (Billing-view entry point),
   * the dialog shows an invoice picker with a patient-level option.
   */
  lockInvoice?: boolean;
  orgId: string;
  /** Called after a successful insert so callers can refresh, toast, etc. */
  onRecorded?: () => void;
  /**
   * When set, the dialog edits this existing payment instead of creating a
   * new one: the form is prefilled from it, the no-overpayment cap excludes
   * its current amount, and submit UPDATEs the row in place.
   */
  editPayment?: InvoicePaymentRow | null;
}

// ─── Helpers ───

function todayISODate(): string {
  const d = new Date();
  // toISOString gives UTC; use local Y-M-D so "today" matches the user's clock.
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Translate a date input (YYYY-MM-DD) into a full ISO timestamp anchored
 * to local noon. Noon dodges DST/timezone-boundary surprises when the
 * record is read back from servers in another timezone.
 */
function visitDateToIso(date: string): string {
  const [y, m, d] = date.split('-').map((s) => parseInt(s, 10));
  if (!y || !m || !d) return new Date().toISOString();
  return new Date(y, m - 1, d, 12, 0, 0).toISOString();
}

// ─── Component ───

export function RecordPaymentDialog({
  open,
  onOpenChange,
  invoice,
  patientId,
  lockInvoice = true,
  orgId,
  onRecorded,
  editPayment,
}: Props) {
  const isEditing = !!editPayment;
  const { db } = usePowerSyncDb();
  const { user } = useAuth();
  const fmt = useFormatMoney();
  const regional = useRegionalSettings();
  const currencySymbol = regional.money_format.currency_symbol;

  // Form fields.
  const [pickedInvoiceId, setPickedInvoiceId] = useState<string>('');
  const [amountInput, setAmountInput] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [notes, setNotes] = useState('');
  const [visitDate, setVisitDate] = useState<string>(() => todayISODate());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // PRD.244 — optional item-level allocation. `allocByItem` maps invoice_item
  // id → amount input string. Empty = unallocated (invoice-level payment).
  const [allocOpen, setAllocOpen] = useState(false);
  const [allocByItem, setAllocByItem] = useState<Record<string, string>>({});
  // True once the user manually edits allocations — stops the default
  // auto-allocation effect from overwriting their choices.
  const [allocTouched, setAllocTouched] = useState(false);

  // PRD.229 §5.6 — Quick Pay UX: when the dialog opens with sensible
  // defaults already prefilled (amount = remaining, method = Cash,
  // date = today), the user can just press Enter to confirm. Focusing
  // the submit button on open enables that single-keystroke flow.
  const submitRef = useRef<HTMLButtonElement>(null);

  // Picker source — only loaded when the dialog is open AND no invoice was
  // pinned by the caller, so invoice-scoped opens stay cheap.
  const pickerEnabled = open && !invoice && !lockInvoice && !!patientId;
  const { data: patientInvoices = [] } = usePowerSyncQuery<InvoiceRow>(
    pickerEnabled
      ? qInvoicesByPatient(patientId)
      : {
          key: ['noop-patient-invoices-payments'] as const,
          sql: 'SELECT NULL WHERE 0',
          params: [],
          watch: [],
        },
  );

  // Effective invoice — either the one passed in by the caller (locked
  // mode) or the one the user picked from the dropdown.
  const effectiveInvoice: InvoiceForPayment | null = useMemo(() => {
    if (invoice) return invoice;
    if (!pickedInvoiceId) return null;
    const row = patientInvoices.find((r) => r.id === pickedInvoiceId);
    if (!row) return null;
    return {
      id: row.id,
      invoice_no: row.invoice_no,
      grand_total: row.grand_total ?? 0,
      status: row.status,
    };
  }, [invoice, pickedInvoiceId, patientInvoices]);

  // Live paid total for whichever invoice is currently effective.
  const { data: paidRows = [] } = usePowerSyncQuery<InvoicePaidTotalRow>(
    effectiveInvoice
      ? qInvoicePaidTotalCents(effectiveInvoice.id)
      : {
          key: ['noop-paid-total'] as const,
          sql: 'SELECT 0 AS paid_cents WHERE 0',
          params: [],
          watch: [],
        },
  );

  const grandTotal: Money = useMemo(
    () => phpFromMajor(effectiveInvoice?.grand_total ?? 0),
    [effectiveInvoice?.grand_total],
  );
  // When editing, exclude the payment being edited from "paid so far" so the
  // remaining/cap calculations treat the edit as a replacement, not an addition.
  const paidSoFar: Money = useMemo(() => {
    const total = paidRows[0]?.paid_cents ?? 0;
    const exclude =
      editPayment && editPayment.invoice_id && editPayment.invoice_id === effectiveInvoice?.id
        ? (editPayment.amount_cents ?? 0)
        : 0;
    return phpFromCents(Math.max(0, total - exclude));
  }, [paidRows, editPayment, effectiveInvoice?.id]);
  const remaining: Money = useMemo(
    () => subtractMoney(grandTotal, paidSoFar),
    [grandTotal, paidSoFar],
  );

  // PRD.244 — item-level allocation source data, only when an invoice is
  // effective (allocation is meaningless for patient-level payments).
  const allocEnabled = open && !!effectiveInvoice;
  const { data: invoiceItems = [] } = usePowerSyncQuery<InvoiceItemRow>(
    allocEnabled
      ? qInvoiceItems(effectiveInvoice!.id)
      : { key: ['noop-alloc-items'] as const, sql: 'SELECT NULL WHERE 0', params: [], watch: [] },
  );
  const { data: existingPayments = [] } = usePowerSyncQuery<InvoicePaymentRow>(
    allocEnabled
      ? qInvoicePaymentsByInvoice(effectiveInvoice!.id)
      : { key: ['noop-alloc-pays'] as const, sql: 'SELECT NULL WHERE 0', params: [], watch: [] },
  );

  const itemCharges = useMemo(() => {
    const m = new Map<string, number>();
    for (const it of invoiceItems) m.set(it.id, itemChargeCents(it));
    return m;
  }, [invoiceItems]);
  // Already-paid per item from prior payments' explicit allocations. When
  // editing, the payment under edit is excluded so its own allocations don't
  // count against the per-item outstanding it's allowed to fill.
  const alreadyPaidByItem = useMemo(() => {
    const known = new Set(invoiceItems.map((i) => i.id));
    const others = editPayment
      ? existingPayments.filter((p) => p.id !== editPayment.id)
      : existingPayments;
    return aggregatePaymentAllocations(others, known).allocatedByItem;
  }, [existingPayments, invoiceItems, editPayment]);

  // Reset/prefill the form whenever the dialog (re)opens, the invoice changes,
  // or the payment-under-edit changes.
  useEffect(() => {
    if (!open) return;
    setPickedInvoiceId('');
    setSubmitting(false);
    setError(null);

    if (editPayment) {
      // Edit mode — prefill from the existing payment (user's allocations win).
      setAllocTouched(true);
      setMethod((editPayment.method as PaymentMethod) ?? 'cash');
      setNotes(editPayment.notes ?? '');
      setVisitDate(
        (editPayment.paid_at ?? editPayment.created_at ?? '').slice(0, 10) || todayISODate(),
      );
      setAmountInput(((editPayment.amount_cents ?? 0) / 100).toFixed(2));
      const allocs = parsePaymentAllocations(editPayment.allocations);
      const next: Record<string, string> = {};
      for (const a of allocs) next[a.item_id] = (a.amount_cents / 100).toFixed(2);
      setAllocByItem(next);
      setAllocOpen(allocs.length > 0);
      return;
    }

    setMethod('cash');
    setNotes('');
    setVisitDate(todayISODate());
    setAllocOpen(false);
    setAllocByItem({});
    setAllocTouched(false);
    // In invoice-locked mode, default amount to the remaining balance so
    // the common "pay in full" case is one click. In patient-level mode
    // we start empty — there's no remaining to default against.
    if (invoice) {
      const remainingMajor = remaining.toRoundedUnit(2);
      setAmountInput(remainingMajor > 0 ? remainingMajor.toFixed(2) : '');
    } else {
      setAmountInput('');
    }
    // `remaining` intentionally not in deps — we only seed the amount
    // when the dialog opens or the invoice changes, not on every paid-row update.
  }, [open, invoice?.id, editPayment?.id]);

  // PRD.229 §5.6 — Focus the submit button on open so the user can
  // confirm with Enter when defaults are already correct (Quick Pay
  // flow). Modal portals through `mounted` + `visible` flags so the
  // button isn't in the DOM on the first render after `open` flips
  // true. Poll across a handful of frames until the ref is set, then
  // focus — also keeps us safe against the Modal's own focus-trap,
  // which auto-focuses the first focusable on mount.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let attempts = 0;
    function tick() {
      if (cancelled) return;
      const btn = submitRef.current;
      if (btn && !btn.disabled) {
        btn.focus();
        return;
      }
      if (attempts++ < 20) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
    return () => {
      cancelled = true;
    };
  }, [open]);

  const parsed = useMemo(() => parseAmountInput(amountInput), [amountInput]);

  // PRD.244 — running allocation total vs the payment amount. Allocation is
  // optional, so a sum below the payment is fine (the remainder is an
  // unallocated, invoice-level payment); a sum ABOVE the payment is invalid.
  const paymentCents = parsed ? toCents(parsed) : 0;
  const allocatedCents = useMemo(
    () => Object.values(allocByItem).reduce((s, v) => s + inputToCents(v), 0),
    [allocByItem],
  );
  const allocOver = allocatedCents > paymentCents;
  // Once the user starts allocating, they must allocate the WHOLE payment
  // (allocate fully or not at all) — a half-allocated payment is blocked so
  // it can't silently leave an unallocated remainder.
  const allocPartial = allocatedCents > 0 && allocatedCents < paymentCents;

  // PRD.252 — default to auto-allocating the payment across the invoice's
  // outstanding items (single outstanding item → assigned in full). Re-runs as
  // the amount changes until the user edits an allocation manually, after which
  // they're in control. Skipped while editing an existing payment.
  useEffect(() => {
    if (!open || editPayment || allocTouched) return;
    if (!effectiveInvoice || invoiceItems.length === 0) return;
    const next = computeAutoDistribute(paymentCents);
    setAllocByItem(next);
    if (Object.keys(next).length > 0) setAllocOpen(true);
  }, [
    open,
    editPayment,
    allocTouched,
    effectiveInvoice?.id,
    invoiceItems,
    alreadyPaidByItem,
    paymentCents,
  ]);

  // Validation rules:
  //   - amount must parse and be positive
  //   - if an invoice is effective, can't exceed its remaining balance
  //   - patient-level mode (no effective invoice) has no max cap
  //   - item allocations (if any) must not exceed the payment amount
  const exceedsRemaining = !!effectiveInvoice && parsed != null && greaterThan(parsed, remaining);
  const canSubmit =
    !!db &&
    !!patientId &&
    !submitting &&
    parsed != null &&
    isPositive(parsed) &&
    !exceedsRemaining &&
    !allocOver &&
    !allocPartial;

  // Distribute `amountCents` across the invoice's outstanding items, first
  // item first, capped at each item's outstanding. Exactly one outstanding
  // item → the whole amount lands on it (PRD.252 §5.7.1).
  function computeAutoDistribute(amountCents: number): Record<string, string> {
    let remainingCents = amountCents;
    const next: Record<string, string> = {};
    for (const it of invoiceItems) {
      if (remainingCents <= 0) break;
      const charge = itemCharges.get(it.id) ?? 0;
      const already = alreadyPaidByItem.get(it.id) ?? 0;
      const outstanding = Math.max(0, charge - already);
      const give = Math.min(outstanding, remainingCents);
      if (give > 0) next[it.id] = (give / 100).toFixed(2);
      remainingCents -= give;
    }
    return next;
  }

  function autoDistribute() {
    if (paymentCents <= 0) return;
    setAllocTouched(false);
    setAllocByItem(computeAutoDistribute(paymentCents));
    setAllocOpen(true);
  }

  // PRD.244 — fully allocate a single item: set it to its outstanding, capped
  // by whatever payment amount is still unallocated (so it never overshoots).
  function allocateItemFull(itemId: string) {
    setAllocTouched(true);
    const charge = itemCharges.get(itemId) ?? 0;
    const already = alreadyPaidByItem.get(itemId) ?? 0;
    const outstanding = Math.max(0, charge - already);
    const otherAllocated = allocatedCents - inputToCents(allocByItem[itemId]);
    const available = Math.max(0, paymentCents - otherAllocated);
    const give = Math.min(outstanding, available);
    setAllocByItem((prev) => ({ ...prev, [itemId]: give > 0 ? (give / 100).toFixed(2) : '' }));
    if (error) setError(null);
  }

  async function handleSubmit() {
    if (!db || !patientId || !parsed) return;
    if (!isPositive(parsed)) {
      setError('Amount must be greater than zero.');
      return;
    }
    if (exceedsRemaining) {
      setError(`Amount exceeds remaining balance (${fmt(remaining)}).`);
      return;
    }
    if (allocOver) {
      setError('Item allocations exceed the payment amount.');
      return;
    }
    if (allocPartial) {
      setError('Allocate the full payment amount across items, or clear all allocations.');
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      const now = new Date().toISOString();
      const paidAt = visitDateToIso(visitDate);
      const paymentCents = toCents(parsed);
      const inv = effectiveInvoice;

      // PRD.244 — collect optional item allocations (only positive entries,
      // only when invoice-scoped). null when nothing was allocated.
      const allocations: PaymentAllocation[] = inv
        ? invoiceItems
            .map((it) => ({ item_id: it.id, amount_cents: inputToCents(allocByItem[it.id]) }))
            .filter((a) => a.amount_cents > 0)
        : [];
      const allocationsJson = allocations.length ? JSON.stringify(allocations) : null;
      const newPaidCents = inv ? toCents(paidSoFar) + paymentCents : paymentCents;
      const grandTotalCents = inv ? toCents(grandTotal) : 0;
      const fullySettled = !!inv && newPaidCents >= grandTotalCents && grandTotalCents > 0;

      if (editPayment) {
        await db.execute(
          `UPDATE invoice_payments
              SET amount_cents = ?, method = ?, notes = ?, allocations = ?, paid_at = ?, updated_at = ?, updated_by = ?
            WHERE id = ?`,
          [
            paymentCents,
            method,
            notes.trim() || null,
            allocationsJson,
            paidAt,
            now,
            user?.uid ?? null,
            editPayment.id,
          ],
        );
      } else {
        await db.execute(
          `INSERT INTO invoice_payments
             (id, org_id, invoice_id, patient_id, amount_cents, method, notes, allocations, paid_at, created_at, updated_at, updated_by)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            crypto.randomUUID(),
            orgId,
            inv?.id ?? null,
            patientId,
            paymentCents,
            method,
            notes.trim() || null,
            allocationsJson,
            paidAt,
            now,
            now,
            user?.uid ?? null,
          ],
        );
      }

      // Invoice status + treatment-record mirror follow the post-write balance.
      // Bidirectional so editing a payment down un-settles correctly (the same
      // re-sync the delete-payment path performs).
      if (inv) {
        const nextStatus = fullySettled
          ? 'paid'
          : inv.status === 'draft'
            ? 'issued'
            : (inv.status ?? 'issued');

        if (nextStatus !== inv.status) {
          await db.execute(`UPDATE invoices SET status = ?, updated_at = ? WHERE id = ?`, [
            nextStatus,
            now,
            inv.id,
          ]);
        }

        // PRD.218 §10.1 — keep linked treatment records in sync with settlement:
        // charged when fully settled, cleared otherwise.
        const linked = await db.getAll<{ id: string; amount_charged_cents: number }>(
          `SELECT id, amount_charged_cents FROM patient_treatment_records WHERE invoice_id = ?`,
          [inv.id],
        );
        for (const rec of linked) {
          await db.execute(
            `UPDATE patient_treatment_records
                SET amount_paid_cents = ?, updated_at = ?
              WHERE id = ?`,
            [fullySettled ? rec.amount_charged_cents : 0, now, rec.id],
          );
        }

        // PRD.261 — refresh any patient package tied to this invoice: derive
        // payment_status and activate a still-pending package on first payment.
        await syncPatientPackagePaymentStatusFromInvoice({ db, invoiceId: inv.id });
      }

      onRecorded?.();
      onOpenChange(false);
    } catch (e) {
      console.error('[RecordPaymentDialog] save failed', e);
      setError('Could not save payment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // When editing, the invoice may already be fully paid *because of* this
  // payment, so don't block the form on a zero remaining balance.
  const balanceZero =
    !isEditing && !!effectiveInvoice && !isPositive(remaining) && toCents(remaining) <= 0;

  const headerTitle = isEditing
    ? `Edit Payment${effectiveInvoice ? ` — ${effectiveInvoice.invoice_no ?? 'Invoice'}` : ''}`
    : effectiveInvoice
      ? `Record Payment — ${effectiveInvoice.invoice_no ?? 'Invoice'}`
      : 'Record Payment';
  const headerDescription = effectiveInvoice
    ? `Invoice total ${fmt(grandTotal)} · Paid so far ${fmt(paidSoFar)} · Remaining ${fmt(remaining)}`
    : 'Record a patient payment. Link to a specific invoice or leave patient-level.';

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={headerTitle}
      description={headerDescription}
      maxWidth={460}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
          <Button
            variant="ghost"
            size="md"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            ref={submitRef}
            variant="gradient"
            size="md"
            onClick={handleSubmit}
            disabled={!canSubmit || balanceZero}
          >
            {submitting ? 'Saving…' : isEditing ? 'Save Changes' : 'Record Payment'}
          </Button>
        </div>
      }
    >
      {balanceZero ? (
        <p
          style={{
            margin: 0,
            padding: 'var(--sp-5) var(--sp-6)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-muted)',
          }}
        >
          This invoice is already fully paid.
        </p>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-4)',
            padding: 'var(--sp-5) var(--sp-6)',
          }}
        >
          {/* PRD.229 — Visit Date, always shown. Anchors paid_at. */}
          <Field label="Visit Date">
            <input
              type="date"
              value={visitDate}
              onChange={(e) => setVisitDate(e.target.value || todayISODate())}
              style={inputStyle()}
            />
          </Field>

          {/* PRD.229 — Optional invoice picker, shown only when this dialog
              wasn't opened against a fixed invoice. */}
          {!invoice && !lockInvoice && (
            <Field label="Linked invoice (optional)">
              <select
                value={pickedInvoiceId}
                onChange={(e) => setPickedInvoiceId(e.target.value)}
                style={inputStyle()}
              >
                <option value="">— Patient-level (no invoice link) —</option>
                {patientInvoices.map((inv) => (
                  <option key={inv.id} value={inv.id}>
                    {(inv.invoice_no ?? inv.id.slice(0, 8)) +
                      (inv.date_iso ? ` · ${inv.date_iso}` : '') +
                      (inv.status ? ` · ${inv.status}` : '')}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Amount">
            <div style={{ position: 'relative' }}>
              <span
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)',
                  fontSize: 'var(--font-size-sm)',
                  pointerEvents: 'none',
                }}
              >
                {currencySymbol}
              </span>
              <input
                type="text"
                inputMode="decimal"
                value={amountInput}
                onChange={(e) => {
                  setAmountInput(e.target.value);
                  if (error) setError(null);
                }}
                placeholder="0.00"
                style={{ ...inputStyle(), paddingLeft: 24 }}
              />
            </div>
            {effectiveInvoice && (
              <HintRow>
                <button
                  type="button"
                  onClick={() => {
                    const r = remaining.toRoundedUnit(2);
                    setAmountInput(r > 0 ? r.toFixed(2) : '0');
                  }}
                  style={linkButtonStyle}
                >
                  Pay full balance ({fmt(remaining)})
                </button>
              </HintRow>
            )}
          </Field>

          {/* PRD.244 — optional item-level allocation. Collapsed by default so
              simple invoice-level payments stay one-step. */}
          {effectiveInvoice && invoiceItems.length > 0 && (
            <div
              style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
              }}
            >
              <button
                type="button"
                onClick={() => setAllocOpen((o) => !o)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  padding: '10px 12px',
                  background: 'var(--bg-surface-2)',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 500,
                }}
              >
                <span>
                  Allocate to items{' '}
                  <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span>
                </span>
                <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>
                  {allocatedCents > 0
                    ? `${fmt(phpFromCents(allocatedCents))} allocated`
                    : 'Not allocated'}{' '}
                  {allocOpen ? '▴' : '▾'}
                </span>
              </button>

              {allocOpen && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    padding: '10px 12px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={autoDistribute}
                      disabled={paymentCents <= 0}
                      style={{
                        ...linkButtonStyle,
                        opacity: paymentCents <= 0 ? 0.5 : 1,
                        cursor: paymentCents <= 0 ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Auto-distribute
                    </button>
                  </div>

                  {invoiceItems.map((it) => {
                    const charge = itemCharges.get(it.id) ?? 0;
                    const already = alreadyPaidByItem.get(it.id) ?? 0;
                    const outstanding = Math.max(0, charge - already);
                    const name =
                      it.service_name || it.description_snapshot || it.free_text_name || 'Item';
                    return (
                      <div
                        key={it.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr auto 100px auto auto',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 'var(--font-size-sm)',
                              color: 'var(--text)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {name}
                          </div>
                          <div
                            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}
                          >
                            {fmt(phpFromCents(charge))} charge · {fmt(phpFromCents(outstanding))}{' '}
                            outstanding
                          </div>
                        </div>
                        <span
                          style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}
                        >
                          {currencySymbol}
                        </span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={allocByItem[it.id] ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setAllocTouched(true);
                            setAllocByItem((prev) => ({ ...prev, [it.id]: v }));
                            if (error) setError(null);
                          }}
                          placeholder="0.00"
                          style={{ ...inputStyle(), height: 32, textAlign: 'right' }}
                        />
                        <button
                          type="button"
                          onClick={() => allocateItemFull(it.id)}
                          disabled={paymentCents <= 0 || outstanding <= 0}
                          title="Allocate this item's full outstanding"
                          style={{
                            height: 32,
                            padding: '0 10px',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--border)',
                            background: 'var(--bg-surface)',
                            color:
                              paymentCents <= 0 || outstanding <= 0
                                ? 'var(--text-placeholder)'
                                : 'var(--color-primary, #4f46e5)',
                            fontSize: 'var(--font-size-xs)',
                            fontWeight: 600,
                            cursor:
                              paymentCents <= 0 || outstanding <= 0 ? 'not-allowed' : 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Full
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setAllocTouched(true);
                            setAllocByItem((prev) => ({ ...prev, [it.id]: '' }));
                            if (error) setError(null);
                          }}
                          disabled={inputToCents(allocByItem[it.id]) <= 0}
                          title="Clear this item's allocation"
                          style={{
                            height: 32,
                            padding: '0 10px',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--border)',
                            background: 'var(--bg-surface)',
                            color:
                              inputToCents(allocByItem[it.id]) <= 0
                                ? 'var(--text-placeholder)'
                                : 'var(--text-muted)',
                            fontSize: 'var(--font-size-xs)',
                            fontWeight: 600,
                            cursor:
                              inputToCents(allocByItem[it.id]) <= 0 ? 'not-allowed' : 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Clear
                        </button>
                      </div>
                    );
                  })}

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      borderTop: '1px solid var(--border)',
                      paddingTop: 8,
                      fontSize: 'var(--font-size-xs)',
                    }}
                  >
                    <span style={{ color: 'var(--text-muted)' }}>
                      Allocated {fmt(phpFromCents(allocatedCents))} of{' '}
                      {fmt(phpFromCents(paymentCents))}
                    </span>
                    {allocOver ? (
                      <span style={{ color: 'var(--color-danger-text, #b91c1c)', fontWeight: 600 }}>
                        Exceeds payment
                      </span>
                    ) : allocPartial ? (
                      <span style={{ color: 'var(--color-warning-text)', fontWeight: 600 }}>
                        {fmt(phpFromCents(paymentCents - allocatedCents))} left to allocate
                      </span>
                    ) : allocatedCents === 0 ? (
                      <span style={{ color: 'var(--text-muted)' }}>Not allocated</span>
                    ) : (
                      <span style={{ color: 'var(--color-success-text)', fontWeight: 600 }}>
                        Fully allocated
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <Field label="Method">
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value as PaymentMethod)}
              style={inputStyle()}
            >
              {METHOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Note (optional)">
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. OR #1234, partial deposit"
              style={inputStyle()}
            />
          </Field>

          {error && (
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-danger-text, #b91c1c)',
                background: 'var(--color-danger-bg, #fef2f2)',
                border: '1px solid var(--color-danger-border, #fecaca)',
                padding: 'var(--sp-2) var(--sp-3)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              {error}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ─── Small layout helpers ───

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 500,
          color: 'var(--text-muted)',
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function HintRow({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: 4,
      }}
    >
      {children}
    </div>
  );
}

function inputStyle(): React.CSSProperties {
  return {
    width: '100%',
    height: 36,
    padding: '0 12px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border)',
    background: 'var(--bg-surface)',
    color: 'var(--text)',
    fontSize: 'var(--font-size-sm)',
  };
}

const linkButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--color-primary, #4f46e5)',
  fontSize: 'var(--font-size-xs)',
  cursor: 'pointer',
  padding: 0,
};
